import { useCallback, useEffect, useMemo, useState } from 'react'
import { Bike, Check, Printer, ShoppingBag, X } from 'lucide-react'
import {
  CanalPedido,
  dataOperacional,
  formatarReais,
  Papel,
  proximosStatus,
  StatusPedido,
  TipoEntrega,
} from '@du/shared'
import {
  Badge, Button, EmptyState, Icone, KanbanColumn, Modal, OrderCard, Textarea, useAviso,
} from '@du/ui'
import { api, ErroApi } from '../lib/api.js'
import { useSessao } from '../lib/sessao.js'
import type { EventoSse } from '../lib/tempoReal.js'

type PedidoKanban = {
  id: string
  numeroDia: number
  dataOperacional: string
  status: StatusPedido
  canal: CanalPedido
  tipo: TipoEntrega
  clienteNome: string
  bairroNome: string | null
  criadoEm: string
  prometidoPara: string | null
  totalCents?: number
  formaPagamento?: string
  motoboy: { id: string; nome: string } | null
  itens: {
    nomeSnapshot: string
    quantidade: number
    observacao: string | null
    adicionais: { nomeSnapshot: string; grupoSnapshot: string }[]
  }[]
}

/** Colunas do Kanban. SAIU e ENTREGUE ficam juntas: o que importa é "na rua". */
const COLUNAS = [
  { titulo: 'Novos', status: [StatusPedido.RECEBIDO], tom: 'novo' as const },
  { titulo: 'Aceitos', status: [StatusPedido.ACEITO], tom: 'ativo' as const },
  { titulo: 'Na chapa', status: [StatusPedido.EM_PREPARO], tom: 'ativo' as const },
  { titulo: 'Prontos', status: [StatusPedido.PRONTO], tom: 'pronto' as const },
  { titulo: 'Na rua', status: [StatusPedido.SAIU_PARA_ENTREGA], tom: 'neutro' as const },
]

const ROTULO_ACAO: Partial<Record<StatusPedido, string>> = {
  [StatusPedido.ACEITO]: 'Aceitar',
  [StatusPedido.EM_PREPARO]: 'Pôr na chapa',
  [StatusPedido.PRONTO]: 'Pronto',
  [StatusPedido.SAIU_PARA_ENTREGA]: 'Saiu',
  [StatusPedido.ENTREGUE]: 'Entregue',
  [StatusPedido.CANCELADO]: 'Cancelar',
}

const minutosDesde = (iso: string, agora: number) =>
  Math.max(0, Math.floor((agora - new Date(iso).getTime()) / 60_000))

export function Kanban({ registrar }: { registrar: (fn: (e: EventoSse) => void) => void }) {
  const { usuario } = useSessao()
  const { avisar } = useAviso()
  const [pedidos, setPedidos] = useState<PedidoKanban[]>([])
  const [motoboys, setMotoboys] = useState<{ id: string; nome: string }[]>([])
  const [carregando, setCarregando] = useState(true)
  const [agora, setAgora] = useState(() => Date.now())
  const [cancelando, setCancelando] = useState<PedidoKanban | null>(null)
  const [motivo, setMotivo] = useState('')

  const recarregar = useCallback(async () => {
    try {
      setPedidos(await api<PedidoKanban[]>('/v1/pedidos?limite=120'))
    } catch (e) {
      if (e instanceof ErroApi) avisar({ tom: 'erro', texto: 'Não consegui carregar os pedidos', detalhe: e.message })
    } finally {
      setCarregando(false)
    }
  }, [avisar])

  useEffect(() => {
    void recarregar()
  }, [recarregar])

  /**
   * A lista de motoboys muda raramente — carrega uma vez e fica.
   * Recarregar junto com os pedidos (a cada evento SSE) seria uma consulta a
   * mais por pedido novo numa tela que já fica aberta a noite inteira.
   */
  useEffect(() => {
    void (async () => {
      try {
        setMotoboys(await api<{ id: string; nome: string }[]>('/v1/motoboys'))
      } catch {
        // Sem motoboy cadastrado a tela segue normal: o seletor some.
      }
    })()
  }, [])

  // Cronômetro de 20s: suficiente para o minuto virar na tela sem re-renderizar
  // o Kanban inteiro a cada segundo num tablet antigo.
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 20_000)
    return () => clearInterval(t)
  }, [])

  // O SSE traz o resumo do pedido novo, mas a mudança de status vem só com os
  // ids. Recarregar é mais simples e mais seguro que remendar o estado local,
  // e a lista é pequena — um turno inteiro cabe em 120 pedidos.
  useEffect(() => {
    registrar((e) => {
      if (typeof e.tipo === 'string' && e.tipo.startsWith('pedido.')) void recarregar()
    })
  }, [registrar, recarregar])

  // Dia operacional de hoje, para marcar o que sobrou de ontem. O cálculo é o
  // MESMO do servidor (a virada é às 04h, não à meia-noite).
  const hoje = useMemo(() => dataOperacional(new Date()), [agora])

  const porStatus = useMemo(() => {
    const mapa = new Map<StatusPedido, PedidoKanban[]>()
    for (const p of pedidos) {
      const lista = mapa.get(p.status) ?? []
      lista.push(p)
      mapa.set(p.status, lista)
    }
    // Mais antigo primeiro: quem está esperando há mais tempo aparece no topo.
    for (const lista of mapa.values()) {
      lista.sort((a, b) => new Date(a.criadoEm).getTime() - new Date(b.criadoEm).getTime())
    }
    return mapa
  }, [pedidos])

  /**
   * Atribui ou troca o motoboy, direto no cartão.
   *
   * Atualiza o estado local antes da resposta: o atendente está com o
   * telefone na mão e o motoboy na porta, e esperar o servidor para a tela
   * reagir é meio segundo em que ele não sabe se o toque pegou.
   */
  const atribuir = async (pedido: PedidoKanban, motoboyId: string) => {
    const escolhido = motoboys.find((m) => m.id === motoboyId) ?? null
    setPedidos((atuais) =>
      atuais.map((p) => (p.id === pedido.id ? { ...p, motoboy: escolhido } : p)),
    )
    try {
      await api(`/v1/pedidos/${pedido.id}/motoboy`, {
        method: 'PATCH',
        corpo: { motoboyId: motoboyId || null },
      })
    } catch {
      avisar({ tom: 'erro', texto: `Não consegui atribuir o motoboy do #${pedido.numeroDia}` })
      void recarregar()
    }
  }

  const mover = async (pedido: PedidoKanban, para: StatusPedido, motivoTexto?: string) => {
    // Atualização otimista: na cozinha, esperar o servidor para o card sair do
    // lugar faz o atendente clicar de novo achando que não pegou.
    setPedidos((atuais) => atuais.map((p) => (p.id === pedido.id ? { ...p, status: para } : p)))
    try {
      await api(`/v1/pedidos/${pedido.id}/status`, {
        method: 'PATCH',
        corpo: { para, ...(motivoTexto ? { motivo: motivoTexto } : {}) },
      })
    } catch (e) {
      void recarregar()
      avisar({
        tom: 'erro',
        texto: `Não consegui mover o #${pedido.numeroDia}`,
        detalhe: e instanceof ErroApi ? e.message : undefined,
      })
    }
  }

  const reimprimir = async (pedido: PedidoKanban) => {
    try {
      await api(`/v1/pedidos/${pedido.id}/reimprimir`, { method: 'POST', corpo: {} })
      avisar({ tom: 'sucesso', texto: `Comanda do #${pedido.numeroDia} reenviada` })
    } catch (e) {
      avisar({ tom: 'erro', texto: 'Falha ao reimprimir', detalhe: e instanceof ErroApi ? e.message : undefined })
    }
  }

  if (carregando) return <div className="kanban__vazio">Carregando pedidos…</div>

  if (pedidos.length === 0) {
    return (
      <EmptyState
        icone={ShoppingBag}
        titulo="Nenhum pedido agora"
        texto="Assim que entrar pedido, ele aparece aqui com som e destaque."
      />
    )
  }

  return (
    <>
      <div className="kanban">
        {COLUNAS.map((coluna) => {
          const lista = coluna.status.flatMap((s) => porStatus.get(s) ?? [])
          return (
            <KanbanColumn key={coluna.titulo} titulo={coluna.titulo} quantidade={lista.length} tom={coluna.tom}>
              {lista.length === 0 ? (
                <p className="kanban__coluna-vazia">—</p>
              ) : (
                lista.map((p) => {
                  const atraso = p.prometidoPara
                    ? Math.floor((agora - new Date(p.prometidoPara).getTime()) / 60_000)
                    : 0
                  const proximos = proximosStatus(p.status, usuario?.papel ?? Papel.ATENDENTE, p.tipo)
                  const avancar = proximos.find((s) => s !== StatusPedido.CANCELADO)
                  return (
                    <div className="kanban__item" key={p.id}>
                      {p.dataOperacional !== hoje ? (
                        <div className="kanban__de-ontem">
                          <Badge tom="alerta">
                            De {new Date(`${p.dataOperacional}T12:00:00`).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                            })}
                          </Badge>
                          <span>ainda aberto</span>
                        </div>
                      ) : null}
                      <OrderCard
                        numeroDia={p.numeroDia}
                        status={p.status}
                        canal={p.canal}
                        tipo={p.tipo}
                        clienteNome={p.clienteNome}
                        bairroNome={p.bairroNome}
                        minutosDesde={minutosDesde(p.criadoEm, agora)}
                        {...(atraso > 0 ? { atrasadoMin: atraso } : {})}
                        {...(p.totalCents !== undefined ? { totalCents: p.totalCents } : {})}
                        itens={p.itens.map((i) => ({
                          nome: i.nomeSnapshot,
                          quantidade: i.quantidade,
                          detalhes: i.adicionais.map((a) => a.nomeSnapshot),
                          observacao: i.observacao,
                        }))}
                      />
                      {/*
                        Só em ENTREGA, e só a partir de PRONTO: antes disso o
                        pedido ainda está na chapa e ninguém sabe quem vai
                        levar. Some quando não há motoboy cadastrado — seletor
                        vazio é só um lugar a mais para tocar sem efeito.
                      */}
                      {p.tipo === TipoEntrega.ENTREGA &&
                      motoboys.length > 0 &&
                      (p.status === StatusPedido.PRONTO ||
                        p.status === StatusPedido.SAIU_PARA_ENTREGA) ? (
                        <label className="kanban__motoboy">
                          <Icone de={Bike} tamanho={16} />
                          <span className="kanban__motoboy__rotulo">Motoboy</span>
                          <select
                            value={p.motoboy?.id ?? ''}
                            onChange={(e) => void atribuir(p, e.target.value)}
                            aria-label={`Motoboy do pedido ${p.numeroDia}`}
                          >
                            <option value="">a definir</option>
                            {motoboys.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.nome}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}

                      <div className="kanban__acoes">
                        {avancar ? (
                          <Button
                            tamanho="sm"
                            icone={avancar === StatusPedido.SAIU_PARA_ENTREGA ? Bike : Check}
                            onClick={() => void mover(p, avancar)}
                          >
                            {ROTULO_ACAO[avancar] ?? avancar}
                          </Button>
                        ) : null}
                        <Button
                          variante="ghost"
                          tamanho="sm"
                          icone={Printer}
                          onClick={() => void reimprimir(p)}
                          aria-label={`Reimprimir comanda do pedido ${p.numeroDia}`}
                        >
                          Via
                        </Button>
                        {proximos.includes(StatusPedido.CANCELADO) ? (
                          <Button
                            variante="ghost"
                            tamanho="sm"
                            icone={X}
                            onClick={() => {
                              setCancelando(p)
                              setMotivo('')
                            }}
                            aria-label={`Cancelar pedido ${p.numeroDia}`}
                          />
                        ) : null}
                      </div>
                    </div>
                  )
                })
              )}
            </KanbanColumn>
          )
        })}
      </div>

      <Modal
        aberto={cancelando !== null}
        aoFechar={() => setCancelando(null)}
        titulo={cancelando ? `Cancelar pedido #${String(cancelando.numeroDia).padStart(3, '0')}` : ''}
        folha
        rodape={
          <>
            <Button variante="ghost" onClick={() => setCancelando(null)}>
              Voltar
            </Button>
            <Button
              variante="perigo"
              disabled={motivo.trim().length < 3}
              onClick={() => {
                if (!cancelando) return
                void mover(cancelando, StatusPedido.CANCELADO, motivo.trim())
                setCancelando(null)
              }}
            >
              Cancelar pedido
            </Button>
          </>
        }
      >
        <p className="kanban__aviso-cancelar">
          {cancelando?.clienteNome} recebe aviso do cancelamento. O motivo fica no histórico do pedido.
        </p>
        {cancelando?.totalCents !== undefined ? (
          <p>
            <Badge tom="neutro">{formatarReais(cancelando.totalCents)}</Badge>
          </p>
        ) : null}
        <Textarea
          rotulo="Motivo"
          required
          maxLength={200}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Produto em falta, endereço fora de área, cliente desistiu…"
        />
      </Modal>
    </>
  )
}
